import { Component, Input } from '@angular/core';

import { ParticipantPriceStatus } from '@cds/betting-offer';
import { OptionGroupType } from '@frontend/sports/betting-offer/feature/fixture-factories';
import { EventOption, EventOptionGroup, Tv2MarketParameters } from '@frontend/sports/betting-offer/feature/model';
import { MarketHelpConfig } from '@frontend/sports/common/client-config-data-access';
import { UrlHelperService } from '@frontend/sports/navigation/core/feature/url-helper';
import { HorsesRaceComponentViewModel, MarketView, RacingOptionMarketTypes, WinGameFixture } from '@frontend/sports/racing/feature/models';
import { clamp } from 'lodash-es';
import { Subject } from 'rxjs';

import { BestOddsGuaranteedService } from '../../../../best-odds-guarantee/services/best-odds-guaranteed.service';
import { HorsesFixtureHelper } from '../../../../horse-racing-shared/services/horses-fixture-helper.service';
import { HorsesFixtureMarketHelper } from '../../../../horse-racing-shared/services/horses-fixture-market-helper.service';
import { HorsesFixtureParticipantHelper } from '../../../../horse-racing-shared/services/horses-fixture-participant-helper.service';
import { HorsesFixturePricingHelper } from '../../../../horse-racing-shared/services/horses-fixture-pricing-helper.service';
import { HorsesFixtureTranslationHelper } from '../../../../horse-racing-shared/services/horses-fixture-translation-helper.service';
import { EachWayMarketHelperService } from '../../../../option-group/each-way-market-helper.service';
import { TabBarItem } from '../../../../tab-bar/tab-bar.models';
import { GreyhoundTrackingService } from '../../../common/greyhound-tracking.service';

@Component({
    template: '',
    standalone: false,
})
export abstract class RaceComponent {
    @Input() fixture: WinGameFixture;
    selectTopTwo = new Subject<boolean>();
    selectTopThree = new Subject<boolean>();
    @Input() isGreyhounds: boolean;
    marketViews = MarketView;
    selectedMarketTitle: string | undefined;
    marketTabs: TabBarItem[];

    optionGroups: EventOptionGroup[];
    optionGroup?: EventOptionGroup;

    subMarkets: TabBarItem[] = [];
    subSelectedMarketId: number | undefined;
    displayType: string;
    showMarketHelp: boolean;
    groupTypes = OptionGroupType;
    marketType?: string;
    isOptionBet: boolean;
    selectedMarketId: number | undefined;
    participantOptionGroups: { [id: number]: EventOption[] } = {};
    racingOptionMarketTypes = RacingOptionMarketTypes;

    constructor(
        protected horsesFixtureMarketHelper: HorsesFixtureMarketHelper,
        protected horsesFixtureTranslationHelper: HorsesFixtureTranslationHelper,
        protected eachWayMarketHelperService: EachWayMarketHelperService,
        protected horsesFixtureHelper: HorsesFixtureHelper,
        protected horsesFixtureParticipantHelper: HorsesFixtureParticipantHelper,
        protected horsesFixturePricingHelper: HorsesFixturePricingHelper,
        protected greyhoundTrackingService: GreyhoundTrackingService,
        protected marketHelpConfig: MarketHelpConfig,
        protected urlHelper: UrlHelperService,
        protected bogService?: BestOddsGuaranteedService,
    ) {}

    vm: HorsesRaceComponentViewModel = {
        selectedMarketView: null,
        prices: {},
        marketViews: [],
        priceStatusVisible: ParticipantPriceStatus.Visible,
        winMarketSuspended: false,
        canSelectTopForecast: false,
        canSelectTopTricast: false,
    };

    protected onSwipe(direction: 'left' | 'right'): void {
        if (this.vm.selectedMarketView) {
            const increment = direction === 'right' ? -1 : 1;
            const crtMarketIdx = this.getMarketViewIndex(this.vm.selectedMarketView);
            if (crtMarketIdx === -1) {
                this.vm.selectedMarketView = this.getFirstMarketViewIfAny();
            }
            const newSelectedIdx = clamp(crtMarketIdx + increment, 0, this.vm.marketViews.length - 1);
            this.vm.selectedMarketView = this.vm.marketViews[newSelectedIdx]
                ? this.vm.marketViews[newSelectedIdx].view
                : this.getFirstMarketViewIfAny();
        }
    }

    getMarketViewIndex(view: MarketView): number {
        return this.vm.marketViews.findIndex((item: { view: MarketView }) => item.view === view);
    }

    getFirstMarketViewIfAny(): MarketView | null {
        return this.vm.marketViews.length > 0 ? this.vm.marketViews[0].view : null;
    }

    onSelectTopTwo(): void {
        this.selectTopTwo.next(true);
    }

    onSelectTopThree(): void {
        this.selectTopThree.next(true);
    }

    getPickTrackingSource(sport: string): string {
        const sportRaceCard = `${sport}_Racecard/`;
        return this.displayType === this.groupTypes.Wincast || (this.isGreyhounds && this.displayType === this.groupTypes.Regular)
            ? sportRaceCard
            : sportRaceCard + this.selectedMarketTitle?.replaceAll(' ', '').replace('+', 'plus').toLowerCase();
    }

    getFirstSubMarketIdIfAny(): number | undefined {
        return this.subMarkets && this.subMarkets.length ? this.subMarkets[0].id : undefined;
    }

    getOptionGroup(optionGroups: EventOptionGroup[]): EventOptionGroup | undefined {
        if (this.subMarkets && this.subMarkets.length && this.subSelectedMarketId) {
            return optionGroups.find((og) => Number(og.id) === this.subSelectedMarketId);
        }

        return optionGroups[0];
    }

    getFirstMarketIdIfAny(): number | undefined {
        return this.marketTabs && this.marketTabs.length ? this.marketTabs[0].id : undefined;
    }

    protected getSubMarkets(optionGroups: EventOptionGroup[], displayType?: string): TabBarItem[] {
        if (displayType === this.groupTypes.Outrights) {
            return [];
        }

        const isWinBylengths = optionGroups.find((t) =>
            this.horsesFixtureMarketHelper.checkRacingOptionMarketTypes(
                RacingOptionMarketTypes.WinByXlengths,
                t.parameters?.find((p) => p.key === Tv2MarketParameters.MarketType)?.value,
            ),
        );
        const markets = optionGroups.map((og) => ({
            id: Number(og.id),
            title: this.horsesFixtureTranslationHelper.getTranslatedMarketName(
                og.parameters?.find((p) => p.key === Tv2MarketParameters.MarketType)?.value!,
                og.originalParameters?.find((param) =>
                    isWinBylengths ? param.key === Tv2MarketParameters.Lengths : param.key === Tv2MarketParameters.Places,
                )?.value,
                og.parameters?.find((p) => p.key === Tv2MarketParameters.MarketType)?.value === RacingOptionMarketTypes.WinEwExtraPlace
                    ? this.eachWayMarketHelperService.calculateEachWayTerms(og.placeTerms!)
                    : og.player1,
            ),
        }));

        return markets;
    }

    buildParticipantOptionGroups(resetSubMarkets: boolean): void {
        if (this.selectedMarketId !== undefined) {
            this.optionGroups = this.horsesFixtureHelper.filterSelectedMarketOptionGroup(this.fixture, this.selectedMarketId);
        }

        if (this.optionGroups?.length) {
            this.displayType = this.optionGroups[0].detailedGrouping?.displayType;
        }
        if (resetSubMarkets) {
            this.subMarkets = this.getSubMarkets(this.optionGroups, this.displayType);
            this.subSelectedMarketId = this.getFirstSubMarketIdIfAny();
        }
        this.optionGroup = this.getOptionGroup(this.optionGroups);
        if (this.optionGroup) {
            this.showMarketHelp = this.marketHelpConfig.enabled && !!this.optionGroup?.detailedGrouping?.marketHelpPath;
            this.marketType = this.optionGroup?.parameters?.find((p) => p.key === Tv2MarketParameters.MarketType)?.value;

            const { participants } = this.fixture;
            if (this.displayType !== this.groupTypes.Outrights) {
                this.participantOptionGroups = this.horsesFixtureParticipantHelper.getParticipantOptionGroups(
                    this.optionGroup,
                    this.horsesFixtureParticipantHelper.getParticipants(participants),
                );
            } else {
                this.optionGroup.options.forEach((op: EventOption) => {
                    op.online = this.horsesFixturePricingHelper.isV3WinOptionAvailable(op);
                });
            }
        }
    }

    setDerivedMarketViewModel(fixture: WinGameFixture, resetSelectedMarket: boolean): void {
        if (resetSelectedMarket) {
            // When switch to another fixture always select first available market.
            this.marketTabs = this.horsesFixtureMarketHelper.getDerivedMarkets(fixture).filter((x) => x.id !== 10000); //tv3 market tabs
            this.selectedMarketId = this.getFirstMarketIdIfAny();
            this.selectedMarketTitle = this.getFirstMarketTitleIfAny();
            this.subMarkets = [];
            this.subSelectedMarketId = this.getFirstSubMarketIdIfAny();
        } else {
            // When fixture is updated, keep current market selected.
            const currentSelectedMarket = this.selectedMarketTitle && this.marketTabs.find((t) => t.title === this.selectedMarketTitle);
            this.selectedMarketTitle = currentSelectedMarket ? currentSelectedMarket.title : this.getFirstMarketTitleIfAny();
            const currentsubSelectedMarket = this.subSelectedMarketId && this.subMarkets.find((t) => t.id === this.subSelectedMarketId)?.id;
            this.subSelectedMarketId = currentsubSelectedMarket ? currentsubSelectedMarket : this.getFirstSubMarketIdIfAny();
        }
        this.buildParticipantOptionGroups(resetSelectedMarket);
    }

    getFirstMarketTitleIfAny(): string {
        if (this.marketTabs.length) {
            return this.marketTabs[0]?.title ?? '';
        } else return '';
    }

    setDerivedSelectedMarket(selectedTabItem: number) {
        let selectedTab = this.marketTabs.find((tab) => tab.id == selectedTabItem);
        this.selectedMarketId = selectedTab?.id;
        this.selectedMarketTitle = selectedTab?.title;
        const url = this.urlHelper.getRacingUrlByFixture(this.fixture.sport.id, this.fixture);
        this.buildParticipantOptionGroups(true);
        this.greyhoundTrackingService.trackselectMarket(this.fixture, selectedTab?.title ?? '', url);
    }

    setSelectSubMarket(tabItem: TabBarItem): void {
        this.subSelectedMarketId = tabItem.id;
        this.buildParticipantOptionGroups(false);
        this.greyhoundTrackingService.trackSubMarket(tabItem.title, this.selectedMarketTitle, this.fixture);
    }

    abstract setSelectedMarket(selectedTabItem: string): void;
}
